<template>
  <div class="cliente-side-drawer-pane cliente-side-drawer-pane--financeiro p-0">
    <div v-if="loading" class="cliente-financeiro-drawer__loading">
      <p>{{ t('common.loading') }}</p>
    </div>
    <div v-else class="cliente-financeiro-drawer">
      <div class="cliente-financeiro-drawer__toolbar">
        <p class="cliente-financeiro-drawer__hint">{{ t('workspaceClienteModal.financeiro.drawerHint') }}</p>
        <button type="button" class="cliente-financeiro-drawer__add" @click="openRecebivelModal()">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {{ t('workspaceClienteModal.financeiro.add') }}
        </button>
      </div>

      <div v-if="financeiroDrawerStats.total" class="cliente-financeiro-drawer__stats">
        <button
          type="button"
          class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--pending"
          :class="financeiroDrawerStatClass('pending')"
          :aria-pressed="isFinanceiroDrawerFilterActive('pending')"
          @click="toggleFinanceiroDrawerFilter('pending')"
        >
          <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="cliente-financeiro-drawer__stat-body">
            <span class="cliente-financeiro-drawer__stat-label">{{ t('workspaceClienteModal.financeiro.statPending') }}</span>
            <strong class="cliente-financeiro-drawer__stat-value">{{ formatMoneyAmount(financeiroDrawerStats.pendingTotal, locale) }}</strong>
            <span v-if="financeiroDrawerStats.openCount" class="cliente-financeiro-drawer__stat-meta">
              {{ t('workspaceClienteModal.financeiro.statOpenCount', { count: financeiroDrawerStats.openCount }) }}
            </span>
          </div>
        </button>
        <button
          type="button"
          class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--paid"
          :class="financeiroDrawerStatClass('paid')"
          :aria-pressed="isFinanceiroDrawerFilterActive('paid')"
          @click="toggleFinanceiroDrawerFilter('paid')"
        >
          <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="cliente-financeiro-drawer__stat-body">
            <span class="cliente-financeiro-drawer__stat-label">{{ t('workspaceClienteModal.financeiro.statPaid') }}</span>
            <strong class="cliente-financeiro-drawer__stat-value">{{ formatMoneyAmount(financeiroDrawerStats.paidTotal, locale) }}</strong>
          </div>
        </button>
        <button
          v-if="financeiroDrawerStats.overdueCount"
          type="button"
          class="cliente-financeiro-drawer__stat cliente-financeiro-drawer__stat--alert"
          :class="financeiroDrawerStatClass('overdue')"
          :aria-pressed="isFinanceiroDrawerFilterActive('overdue')"
          @click="toggleFinanceiroDrawerFilter('overdue')"
        >
          <div class="cliente-financeiro-drawer__stat-icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="cliente-financeiro-drawer__stat-body">
            <span class="cliente-financeiro-drawer__stat-label">{{ t('workspaceClienteModal.financeiro.statOverdue') }}</span>
            <strong class="cliente-financeiro-drawer__stat-value">{{ financeiroDrawerStats.overdueCount }}</strong>
          </div>
        </button>
      </div>

      <div v-if="!recebiveisOrdenados.length" class="cliente-financeiro-drawer__empty cliente-empty-state">
        <svg class="cliente-empty-state__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        <p>{{ t('workspaceClienteModal.financeiro.empty') }}</p>
        <button type="button" class="cliente-empty-state__cta" @click="openRecebivelModal()">{{ t('workspaceClienteModal.financeiro.add') }}</button>
      </div>

      <div v-else class="cliente-financeiro-drawer__list-wrap">
        <div class="cliente-financeiro-drawer__list-head">
          <h3 class="cliente-financeiro-drawer__list-title">{{ t('workspaceClienteModal.financeiro.title') }}</h3>
          <span class="cliente-financeiro-drawer__list-count">{{ recebiveisDrawerFiltrados.length }}</span>
        </div>
        <div
          v-if="!recebiveisDrawerFiltrados.length"
          class="cliente-financeiro-drawer__filter-empty"
        >
          <p>{{ t('workspaceClienteModal.financeiro.filterEmpty') }}</p>
          <button type="button" class="cliente-financeiro-drawer__filter-reset" @click="resetFinanceiroDrawerFilters">
            {{ t('workspaceClienteModal.financeiro.filterShowAll') }}
          </button>
        </div>
        <div v-else class="cliente-financeiro-drawer__list">
          <ClienteRecebivelCard
            v-for="item in recebiveisDrawerFiltrados"
            :key="item.id"
            variant="drawer"
            :item="item"
            :locale="locale"
            :status-class="recebivelStatusClass(item)"
            :bar-class="recebivelBarClass(item)"
            :badge-class="recebivelBadgeClass(item)"
            :status-label="recebivelStatusLabel(item)"
            :format-date-only="formatDateOnly"
            :show-delete="canDeleteRecebivel"
            @edit="openRecebivelModal"
            @toggle-pago="onToggleRecebivelPago"
            @delete="requestDeleteRecebivel"
          />
        </div>
      </div>
    </div>

    <ClienteRecebivelFormModal
      :open="showRecebivelForm"
      :initial="recebivelEdit"
      :locale="dateLocale"
      @close="closeRecebivelModal"
      @save="onRecebivelSaved"
    />

    <ClienteConfirmModal
      :open="!!confirmDeleteTarget"
      :title="t('workspaceClienteModal.financeiro.deleteConfirmTitle')"
      :message="deleteConfirmMessage"
      :loading="deleting"
      @cancel="cancelDeleteRecebivel"
      @confirm="confirmDeleteRecebivel"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import { formatMoneyAmount } from '@/utils/format'
import { parseRecebiveisJson } from '@/utils/clienteRecebivelApi'
import { useRecebiveisDisplay } from '@/composables/useRecebiveisDisplay'
import { useRecebivelStaffDelete } from '@/composables/useRecebivelStaffDelete'
import ClienteRecebivelCard from '@/components/cliente/ClienteRecebivelCard.vue'
import ClienteRecebivelFormModal from '@/components/cliente/ClienteRecebivelFormModal.vue'
import ClienteConfirmModal from '@/components/cliente/ClienteConfirmModal.vue'
import '@/assets/styles/cliente-modal-theme.css'

const props = defineProps({
  clienteId: { type: Number, required: true },
  workspaceId: { type: Number, required: true },
  active: { type: Boolean, default: false }
})

const emit = defineEmits(['updated'])

const { t, locale } = useI18n()
const {
  formatDateOnly,
  recebivelStatusKey,
  recebivelStatusClass,
  recebivelStatusLabel,
  recebivelBadgeClass,
  recebivelBarClass
} = useRecebiveisDisplay()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const loading = ref(false)
const recebiveis = ref([])
const showRecebivelForm = ref(false)
const recebivelEditingId = ref(null)
const recebivelEdit = ref(null)

const FINANCEIRO_DRAWER_FILTER_KEYS = ['pending', 'paid', 'overdue']
const financeiroDrawerFilters = ref(new Set(FINANCEIRO_DRAWER_FILTER_KEYS))

const recebiveisOrdenados = computed(() =>
  [...recebiveis.value].sort((a, b) => String(a.dataVencimento || '').localeCompare(String(b.dataVencimento || '')))
)

function resetFinanceiroDrawerFilters () {
  financeiroDrawerFilters.value = new Set(FINANCEIRO_DRAWER_FILTER_KEYS)
}

function isFinanceiroDrawerFilterActive (key) {
  return financeiroDrawerFilters.value.has(key)
}

function financeiroDrawerStatClass (key) {
  return isFinanceiroDrawerFilterActive(key)
    ? 'cliente-financeiro-drawer__stat--filter-active'
    : 'cliente-financeiro-drawer__stat--filter-inactive'
}

function toggleFinanceiroDrawerFilter (key) {
  const current = financeiroDrawerFilters.value

  if (current.size === FINANCEIRO_DRAWER_FILTER_KEYS.length) {
    financeiroDrawerFilters.value = new Set([key])
    return
  }

  if (current.size === 1 && current.has(key)) {
    resetFinanceiroDrawerFilters()
    return
  }

  const next = new Set(current)
  if (next.has(key)) {
    if (next.size <= 1) return
    next.delete(key)
  } else {
    next.add(key)
  }
  financeiroDrawerFilters.value = next
}

function recebivelMatchesDrawerFilter (item) {
  const filters = financeiroDrawerFilters.value
  if (filters.size === FINANCEIRO_DRAWER_FILTER_KEYS.length) return true

  const status = recebivelStatusKey(item)
  const isPaid = !!item.pago
  const isOverdue = !isPaid && status === 'overdue'
  const isPendingOpen = !isPaid && status !== 'overdue'

  if (filters.has('paid') && isPaid) return true
  if (filters.has('overdue') && isOverdue) return true
  if (filters.has('pending') && isPendingOpen) return true
  return false
}

const recebiveisDrawerFiltrados = computed(() =>
  recebiveisOrdenados.value.filter(recebivelMatchesDrawerFilter)
)

const financeiroDrawerStats = computed(() => {
  let pendingTotal = 0
  let paidTotal = 0
  let overdueCount = 0
  let openCount = 0
  for (const item of recebiveisOrdenados.value) {
    const valor = Number(item.valor) || 0
    if (item.pago) {
      paidTotal += valor
    } else {
      pendingTotal += valor
      openCount += 1
      if (recebivelStatusKey(item) === 'overdue') overdueCount += 1
    }
  }
  return { pendingTotal, paidTotal, overdueCount, openCount, total: recebiveisOrdenados.value.length }
})

async function loadRecebiveis () {
  if (!props.clienteId) return
  loading.value = true
  try {
    const { data } = await api.get(`/clientes/${props.clienteId}`)
    recebiveis.value = parseRecebiveisJson(data?.recebiveisJson)
  } catch (error) {
    console.error(error)
    recebiveis.value = []
  } finally {
    loading.value = false
  }
}

const workspaceIdRef = toRef(props, 'workspaceId')
const clienteIdRef = toRef(props, 'clienteId')

async function onRecebivelDeleted (list) {
  if (Array.isArray(list)) {
    recebiveis.value = list
  } else {
    await loadRecebiveis()
  }
  emit('updated')
}

const {
  canDeleteRecebivel,
  confirmDeleteTarget,
  deleting,
  loadStaffRole,
  requestDeleteRecebivel,
  cancelDeleteRecebivel,
  confirmDeleteRecebivel
} = useRecebivelStaffDelete({
  workspaceIdRef,
  clienteIdRef,
  onDeleted: onRecebivelDeleted
})

const deleteConfirmMessage = computed(() => {
  const item = confirmDeleteTarget.value
  if (!item) return t('workspaceClienteModal.financeiro.deleteConfirmMessage')
  const valor = formatMoneyAmount(item.valor || 0, locale.value)
  const date = formatDateOnly(item.dataVencimento)
  return t('workspaceClienteModal.financeiro.deleteConfirmMessageDetail', { valor, date })
})

async function saveRecebiveis () {
  if (!props.clienteId) return
  const json = JSON.stringify(recebiveis.value)
  const { data } = await api.put(`/clientes/${props.clienteId}/recebiveis`, { recebiveisJson: json })
  recebiveis.value = parseRecebiveisJson(data?.recebiveisJson)
  emit('updated', data)
}

const openRecebivelModal = (item = null) => {
  if (item) {
    recebivelEditingId.value = item.id
    recebivelEdit.value = {
      valor: item.valor,
      dataVencimento: item.dataVencimento || '',
      descricao: item.descricao || ''
    }
  } else {
    recebivelEditingId.value = null
    recebivelEdit.value = null
  }
  showRecebivelForm.value = true
}

const closeRecebivelModal = () => {
  showRecebivelForm.value = false
  recebivelEditingId.value = null
  recebivelEdit.value = null
}

const onRecebivelSaved = async (payload) => {
  const { valor, dataVencimento, descricao, parcelas } = payload
  if (recebivelEditingId.value) {
    const idx = recebiveis.value.findIndex(r => r.id === recebivelEditingId.value)
    if (idx >= 0) {
      recebiveis.value[idx] = {
        ...recebiveis.value[idx],
        valor,
        dataVencimento,
        descricao: descricao || ''
      }
    }
  } else if (Array.isArray(parcelas) && parcelas.length) {
    recebiveis.value.push(...parcelas)
  } else {
    recebiveis.value.push({
      id: `r_${Date.now()}`,
      valor,
      dataVencimento,
      descricao: descricao || '',
      pago: false,
      dataPagamento: null
    })
  }
  closeRecebivelModal()
  await saveRecebiveis()
}

const onToggleRecebivelPago = async (item) => {
  const idx = recebiveis.value.findIndex(r => r.id === item.id)
  if (idx < 0) return
  const nextPago = !recebiveis.value[idx].pago
  const hoje = new Date().toISOString().slice(0, 10)
  recebiveis.value[idx] = {
    ...recebiveis.value[idx],
    pago: nextPago,
    dataPagamento: nextPago ? hoje : null
  }
  await saveRecebiveis()
}

watch(
  () => [props.clienteId, props.active, props.workspaceId],
  ([id, active, workspaceId]) => {
    if (workspaceId && active) loadStaffRole()
    if (id && active) {
      resetFinanceiroDrawerFilters()
      loadRecebiveis()
    }
  },
  { immediate: true }
)
</script>
